import { useState } from "react";
import { usePayroll } from "@/contexts/PayrollContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Plus, Users, IndianRupee, Download, FileText, Calendar,
  CheckCircle2, Clock, UserCheck, Briefcase, TrendingUp
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

function formatINR(v: number) { return `₹${v.toLocaleString("en-IN")}`; }

const PayrollPage = () => {
  const {
    employees, structures, attendance, payslips,
    addEmployee, updateAttendance, generatePayslips, markPaid,
    downloadPayslipPDF, exportSalaryReport, getAttendance,
  } = usePayroll();
  const { role } = useAuth();
  const { toast } = useToast();
  const [tab, setTab] = useState("employees");
  const [selectedMonth, setSelectedMonth] = useState("2026-03");
  const [empOpen, setEmpOpen] = useState(false);
  const [attOpen, setAttOpen] = useState(false);
  const [attEmpId, setAttEmpId] = useState("");

  // New employee form
  const [fName, setFName] = useState("");
  const [fCode, setFCode] = useState("");
  const [fDesig, setFDesig] = useState("");
  const [fDept, setFDept] = useState("");
  const [fPan, setFPan] = useState("");
  const [fBank, setFBank] = useState("");
  const [fBankName, setFBankName] = useState("");
  const [fDoj, setFDoj] = useState("");
  const [fStructure, setFStructure] = useState(structures[0]?.id || "");

  // Attendance form
  const [attPresent, setAttPresent] = useState(26);
  const [attAbsent, setAttAbsent] = useState(0);
  const [attLeave, setAttLeave] = useState(0);
  const [attOT, setAttOT] = useState(0);

  const canManage = role === "company_admin" || role === "ca";
  const monthPayslips = payslips.filter((p) => p.month === selectedMonth);
  const totalGross = monthPayslips.reduce((s, p) => s + p.grossEarnings, 0);
  const totalNet = monthPayslips.reduce((s, p) => s + p.netSalary, 0);
  const totalTDS = monthPayslips.reduce((s, p) => s + p.tds, 0);

  const months = ["2026-03", "2026-02", "2026-01", "2025-12"];

  const handleAddEmployee = () => {
    if (!fName.trim() || !fCode.trim()) {
      toast({ title: "Error", description: "Name and code are required", variant: "destructive" }); return;
    }
    addEmployee({
      name: fName.trim(), empCode: fCode.trim(), designation: fDesig.trim(),
      department: fDept.trim(), pan: fPan.trim(), bankAccount: fBank.trim(),
      bankName: fBankName.trim(), dateOfJoining: fDoj, salaryStructureId: fStructure,
    });
    toast({ title: "Employee Added", description: fName });
    setEmpOpen(false);
    setFName(""); setFCode(""); setFDesig(""); setFDept(""); setFPan(""); setFBank(""); setFBankName(""); setFDoj("");
  };

  const handleSaveAttendance = () => {
    if (!attEmpId) return;
    updateAttendance(attEmpId, selectedMonth, {
      presentDays: attPresent, absentDays: attAbsent,
      leaveDays: attLeave, overtimeHours: attOT, totalDays: 26,
    });
    toast({ title: "Attendance Updated" });
    setAttOpen(false);
  };

  const handleGenerate = () => {
    const generated = generatePayslips(selectedMonth);
    if (generated.length === 0) {
      toast({ title: "No new payslips", description: "All payslips already generated or attendance missing", variant: "destructive" });
    } else {
      toast({ title: `${generated.length} payslips generated`, description: `Month: ${selectedMonth}` });
    }
  };

  const openAttDialog = (empId: string) => {
    const att = getAttendance(empId, selectedMonth);
    setAttEmpId(empId);
    setAttPresent(att?.presentDays ?? 26);
    setAttAbsent(att?.absentDays ?? 0);
    setAttLeave(att?.leaveDays ?? 0);
    setAttOT(att?.overtimeHours ?? 0);
    setAttOpen(true);
  };

  const inputCls = "w-full px-3 py-2 text-sm rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-accent";
  const labelCls = "text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5";

  const STATUS_STYLES: Record<string, string> = {
    Draft: "bg-muted text-muted-foreground",
    Generated: "bg-info/10 text-info border-info/20",
    Paid: "bg-success/10 text-success border-success/20",
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground">Payroll</h1>
          <p className="text-sm text-muted-foreground">Employee profiles, salary generation, TDS, and payslips</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="text-xs w-32"><Calendar className="h-3 w-3 mr-1" /><SelectValue /></SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m} value={m}>{new Date(m + "-01").toLocaleDateString("en-IN", { month: "short", year: "numeric" })}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canManage && (
            <Button onClick={handleGenerate} className="bg-accent text-accent-foreground hover:bg-accent/90 text-xs">
              <TrendingUp className="h-4 w-4 mr-1" /> Generate Salaries
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: "Employees", value: employees.filter((e) => e.status === "Active").length, icon: Users, color: "text-primary" },
          { label: "Gross Salary", value: formatINR(totalGross), icon: IndianRupee, color: "text-accent" },
          { label: "Net Payout", value: formatINR(totalNet), icon: TrendingUp, color: "text-success" },
          { label: "TDS Deducted", value: formatINR(totalTDS), icon: Briefcase, color: "text-warning" },
          { label: "Payslips", value: monthPayslips.length, icon: FileText, color: "text-info" },
        ].map((s) => (
          <Card key={s.label} className="border-border/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                <s.icon className={`h-5 w-5 ${s.color}`} />
              </div>
              <div>
                <p className="text-lg font-bold text-foreground">{s.value}</p>
                <p className="text-[11px] text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="employees" className="text-xs">Employees</TabsTrigger>
          <TabsTrigger value="attendance" className="text-xs">Attendance</TabsTrigger>
          <TabsTrigger value="payslips" className="text-xs">Payslips</TabsTrigger>
          <TabsTrigger value="structures" className="text-xs">Salary Structures</TabsTrigger>
        </TabsList>

        {/* EMPLOYEES TAB */}
        <TabsContent value="employees">
          <Card className="border-border/50">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Employee Profiles</CardTitle>
              {canManage && (
                <Dialog open={empOpen} onOpenChange={setEmpOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-accent text-accent-foreground"><Plus className="h-4 w-4 mr-1" /> Add Employee</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle className="font-display">New Employee</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div><label className={labelCls}>Full Name</label><input value={fName} onChange={(e) => setFName(e.target.value)} maxLength={100} className={inputCls} /></div>
                        <div><label className={labelCls}>Employee Code</label><input value={fCode} onChange={(e) => setFCode(e.target.value)} maxLength={20} className={inputCls} placeholder="EMP-006" /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><label className={labelCls}>Designation</label><input value={fDesig} onChange={(e) => setFDesig(e.target.value)} maxLength={50} className={inputCls} /></div>
                        <div><label className={labelCls}>Department</label><input value={fDept} onChange={(e) => setFDept(e.target.value)} maxLength={50} className={inputCls} /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><label className={labelCls}>PAN</label><input value={fPan} onChange={(e) => setFPan(e.target.value)} maxLength={10} className={inputCls} /></div>
                        <div><label className={labelCls}>Date of Joining</label><input type="date" value={fDoj} onChange={(e) => setFDoj(e.target.value)} className={inputCls} /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><label className={labelCls}>Bank Name</label><input value={fBankName} onChange={(e) => setFBankName(e.target.value)} maxLength={50} className={inputCls} /></div>
                        <div><label className={labelCls}>Bank Account</label><input value={fBank} onChange={(e) => setFBank(e.target.value)} maxLength={20} className={inputCls} /></div>
                      </div>
                      <div><label className={labelCls}>Salary Structure</label>
                        <Select value={fStructure} onValueChange={setFStructure}><SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>{structures.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} — Basic ₹{s.basic.toLocaleString("en-IN")}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setEmpOpen(false)}>Cancel</Button>
                        <Button onClick={handleAddEmployee} className="bg-accent text-accent-foreground">Add Employee</Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      {["Code", "Name", "Designation", "Department", "PAN", "Bank", "Joined", "Structure", "Status"].map((h) => (
                        <TableHead key={h} className="text-[11px] font-semibold">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.map((emp) => {
                      const struct = structures.find((s) => s.id === emp.salaryStructureId);
                      return (
                        <TableRow key={emp.id} className="hover:bg-muted/20">
                          <TableCell className="text-xs font-mono font-medium">{emp.empCode}</TableCell>
                          <TableCell className="text-xs font-medium">{emp.name}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{emp.designation}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{emp.department}</TableCell>
                          <TableCell className="text-xs font-mono text-muted-foreground">{emp.pan}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{emp.bankName}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{emp.dateOfJoining}</TableCell>
                          <TableCell><Badge variant="outline" className="text-[10px]">{struct?.name || "—"}</Badge></TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-[10px] ${emp.status === "Active" ? "bg-success/10 text-success border-success/20" : "bg-muted text-muted-foreground"}`}>{emp.status}</Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ATTENDANCE TAB */}
        <TabsContent value="attendance">
          <Card className="border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><UserCheck className="h-4 w-4 text-accent" /> Attendance — {new Date(selectedMonth + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      {["Employee", "Total Days", "Present", "Absent", "Leave", "Overtime (hrs)", "Actions"].map((h) => (
                        <TableHead key={h} className="text-[11px] font-semibold">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.filter((e) => e.status === "Active").map((emp) => {
                      const att = getAttendance(emp.id, selectedMonth);
                      return (
                        <TableRow key={emp.id} className="hover:bg-muted/20">
                          <TableCell className="text-xs font-medium">{emp.empCode} — {emp.name}</TableCell>
                          <TableCell className="text-xs">{att?.totalDays ?? "—"}</TableCell>
                          <TableCell className="text-xs font-semibold text-success">{att?.presentDays ?? "—"}</TableCell>
                          <TableCell className="text-xs text-destructive">{att?.absentDays ?? "—"}</TableCell>
                          <TableCell className="text-xs text-warning">{att?.leaveDays ?? "—"}</TableCell>
                          <TableCell className="text-xs">{att?.overtimeHours ?? "—"}</TableCell>
                          <TableCell>
                            {canManage && (
                              <button onClick={() => openAttDialog(emp.id)} className="text-xs text-accent hover:underline">Edit</button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Attendance Edit Dialog */}
          <Dialog open={attOpen} onOpenChange={setAttOpen}>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Update Attendance</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={labelCls}>Present Days</label><input type="number" min="0" max="31" value={attPresent} onChange={(e) => setAttPresent(Number(e.target.value))} className={inputCls} /></div>
                  <div><label className={labelCls}>Absent Days</label><input type="number" min="0" max="31" value={attAbsent} onChange={(e) => setAttAbsent(Number(e.target.value))} className={inputCls} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={labelCls}>Leave Days</label><input type="number" min="0" max="31" value={attLeave} onChange={(e) => setAttLeave(Number(e.target.value))} className={inputCls} /></div>
                  <div><label className={labelCls}>Overtime Hours</label><input type="number" min="0" value={attOT} onChange={(e) => setAttOT(Number(e.target.value))} className={inputCls} /></div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setAttOpen(false)}>Cancel</Button>
                  <Button onClick={handleSaveAttendance} className="bg-accent text-accent-foreground">Save</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* PAYSLIPS TAB */}
        <TabsContent value="payslips">
          <Card className="border-border/50">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Payslips — {new Date(selectedMonth + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</CardTitle>
              {monthPayslips.length > 0 && (
                <Button variant="outline" size="sm" onClick={() => exportSalaryReport(selectedMonth)}>
                  <Download className="h-4 w-4 mr-1" /> Export Report
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {monthPayslips.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No payslips for this month</p>
                  <p className="text-xs mt-1">Fill attendance and click "Generate Salaries"</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        {["Employee", "Days", "Basic", "HRA", "DA", "Gross", "PF", "TDS", "Deductions", "Net Salary", "Status", "Actions"].map((h) => (
                          <TableHead key={h} className="text-[11px] font-semibold">{h}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {monthPayslips.map((ps) => (
                        <TableRow key={ps.id} className="hover:bg-muted/20">
                          <TableCell className="text-xs font-medium">{ps.empCode} — {ps.employeeName}</TableCell>
                          <TableCell className="text-xs">{ps.daysWorked}/{ps.totalDays}</TableCell>
                          <TableCell className="text-xs">{formatINR(ps.basic)}</TableCell>
                          <TableCell className="text-xs">{formatINR(ps.hra)}</TableCell>
                          <TableCell className="text-xs">{formatINR(ps.da)}</TableCell>
                          <TableCell className="text-xs font-semibold">{formatINR(ps.grossEarnings)}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{formatINR(ps.pf)}</TableCell>
                          <TableCell className="text-xs text-warning">{formatINR(ps.tds)}</TableCell>
                          <TableCell className="text-xs text-destructive">{formatINR(ps.totalDeductions)}</TableCell>
                          <TableCell className="text-xs font-bold text-success">{formatINR(ps.netSalary)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[ps.status]}`}>{ps.status}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <button onClick={() => downloadPayslipPDF(ps)} className="p-1 rounded text-accent hover:bg-accent/10" title="Download Payslip">
                                <Download className="h-3.5 w-3.5" />
                              </button>
                              {ps.status === "Generated" && canManage && (
                                <button onClick={() => markPaid(ps.id)} className="p-1 rounded text-success hover:bg-success/10" title="Mark Paid">
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Totals row */}
                      <TableRow className="bg-muted/40 font-semibold">
                        <TableCell colSpan={5} className="text-xs text-right">Totals:</TableCell>
                        <TableCell className="text-xs">{formatINR(totalGross)}</TableCell>
                        <TableCell className="text-xs">{formatINR(monthPayslips.reduce((s, p) => s + p.pf, 0))}</TableCell>
                        <TableCell className="text-xs">{formatINR(totalTDS)}</TableCell>
                        <TableCell className="text-xs">{formatINR(monthPayslips.reduce((s, p) => s + p.totalDeductions, 0))}</TableCell>
                        <TableCell className="text-xs text-success">{formatINR(totalNet)}</TableCell>
                        <TableCell colSpan={2} />
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* STRUCTURES TAB */}
        <TabsContent value="structures">
          <div className="grid md:grid-cols-3 gap-4">
            {structures.map((s) => (
              <Card key={s.id} className="border-border/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">{s.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="space-y-1.5">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Earnings</p>
                    {[
                      { label: "Basic", value: formatINR(s.basic) },
                      { label: "HRA", value: `${s.hra}% of Basic (${formatINR(s.basic * s.hra / 100)})` },
                      { label: "DA", value: `${s.da}% of Basic (${formatINR(s.basic * s.da / 100)})` },
                      { label: "Special Allowance", value: formatINR(s.specialAllowance) },
                    ].map((r) => (
                      <div key={r.label} className="flex justify-between text-xs">
                        <span className="text-muted-foreground">{r.label}</span>
                        <span className="font-medium">{r.value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-border/30 pt-2 space-y-1.5">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Deductions</p>
                    {[
                      { label: "PF", value: `${s.pf}% of Basic` },
                      { label: "ESI", value: s.esi > 0 ? `${s.esi}% of Gross` : "N/A" },
                      { label: "Professional Tax", value: formatINR(s.professionalTax) },
                    ].map((r) => (
                      <div key={r.label} className="flex justify-between text-xs">
                        <span className="text-muted-foreground">{r.label}</span>
                        <span className="font-medium">{r.value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-border/30 pt-2">
                    <div className="flex justify-between text-xs">
                      <span className="font-medium">Gross CTC (Monthly)</span>
                      <span className="font-bold text-primary">
                        {formatINR(s.basic + (s.basic * s.hra / 100) + (s.basic * s.da / 100) + s.specialAllowance)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PayrollPage;
